import React, { useState, useEffect } from 'react';
import {
  Divider,
  Text,
  Title,
  TextInput,
  Flex,
  Box,
  Button,
  Loader,
  Stack,
  Modal,
  Anchor,
  UnstyledButton,
} from '@mantine/core';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import DealerVerification from '@components/RideChallengeApp/DealerVerification';
import {
  createLoyaltyMembership,
  createThirdPartyLoyaltyMembership,
  thirdPartyUserLookup,
} from './helpers';
import submitTroubleTicket from '@util/submitTroubleTicket';
import { HogMembershipCreateError } from './';

export default function HogMembership({
  additionalReqData = {},
  setMembershipInfo = () => {},
  searchParamsHogIdValue = null,
  userInfo = null,
}) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState('user_lookup');
  const [memberInfoToBeConfirmed, setMemberInfoToBeConfirmed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    setTicketSubmitted(false);
  }, [activeStep]);

  function fetchUser() {
    const reqData = {
      ...additionalReqData,
      variety: 1,
      error_flow: true,
    };

    if (searchParamsHogIdValue) {
      reqData.loyalty_membership_ident = searchParamsHogIdValue.toUpperCase();
      reqData.ident = searchParamsHogIdValue.toUpperCase();
      reqData.ident_only = true;
    }

    thirdPartyUserLookup(reqData)
      .then((response) => {
        const res = response[0];

        if (res) {
          if (res.verified && res.phone_match) {
            onVerifiedAndPhoneMatch(res);
          } else {
            setMemberInfoToBeConfirmed(res);
            setActiveStep('member_confirmation');
          }
        } else {
          setActiveStep('intro');
        }
      })
      .catch((err) => {
        setActiveStep('intro');
      });
  }

  function onMemberConfirmation() {
    setLoading(true);
    createLoyaltyMembership({
      variety: 1,
      ...additionalReqData,
      ...memberInfoToBeConfirmed,
    })
      .then(({ data }) => {
        setMembershipInfo(data);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function onVerifiedAndPhoneMatch(usrLookupData) {
    createLoyaltyMembership({
      variety: 1,
      ...additionalReqData,
      ...usrLookupData,
    })
      .then(({ data }) => {
        setMembershipInfo(data);
      })
      .catch((err) => {
        // toast.error(err);
        // setLoading(false);
        toast.error(err);
        setLoading(false);
      });
  }

  if (activeStep === 'user_lookup') {
    return (
      <div>
        <LoadingScreen />
      </div>
    );
  }

  if (activeStep === 'intro') {
    return (
      <div>
        <Stack>
          <Text align="center" c="white" fw={600} size="xl">
            {t('mixer:hog_membership:intro_question')}
          </Text>
          <Button onClick={() => setActiveStep('member_lookup')} size="md">
            {t('common:yes')}
          </Button>
          <Button color="red" onClick={() => setActiveStep('create')} size="md">
            {t('common:no')}
          </Button>
          <Text align="center" c="dimmed" size="sm">
            {t('mixer:hog_membership:intro_question_tooltip')}
          </Text>
        </Stack>
      </div>
    );
  }

  if (activeStep === 'create') {
    return (
      <div>
        <CreateFlow
          reqData={{
            ...additionalReqData,
            variety: 1,
          }}
          showLead={false}
          setMembershipInfo={setMembershipInfo}
          userInfo={userInfo}
        />
      </div>
    );
  }

  if (activeStep === 'member_lookup') {
    return (
      <Box>
        <MemberLookup
          additionalReqData={{
            ...additionalReqData,
            user_id: additionalReqData.user_id,
          }}
          setMemberInfoToBeConfirmed={(e) => {
            setMemberInfoToBeConfirmed(e);
            setActiveStep('member_confirmation');
          }}
          setMembershipInfo={setMembershipInfo}
        >
          <CreateFlowButtons setActiveStep={setActiveStep} />
        </MemberLookup>
      </Box>
    );
  }

  if (activeStep === 'member_confirmation') {
    if (memberInfoToBeConfirmed.enable_error_flow) {
      return (
        <div>
          <HogMembershipCreateError
            serverMessage={memberInfoToBeConfirmed.message}
            errorCode={memberInfoToBeConfirmed.error_code}
            ticketData={{
              ...additionalReqData,
              ...userInfo,
              phone: userInfo?.mobile_phone,
            }}
          />
        </div>
      );
    }

    if (!memberInfoToBeConfirmed.phone_number) {
      return (
        <div>
          <Stack gap="xs">
            <Text c="white" fw={600} align="center" size="xl">
              {t('mixer:hog_membership:missing_phone_number_text')}:
            </Text>
            <Text c="white" align="center" fw={600}>
              {t('mixer:hog_membership:member_id')}:{' '}
              {memberInfoToBeConfirmed.ident}
            </Text>
            <Text c="white" align="center">
              {t('mixer:hog_membership:call_member_services_at')}{' '}
              <Anchor fw={600} c="orange" href="tel:18002582464">
                1-800-258-2464
              </Anchor>{' '}
              (CLUBHOG) {t('mixer:hog_membership:and_provide_your_member_id')}
            </Text>
            {userInfo && (
              <NeedHelpFlow
                submitted={ticketSubmitted}
                reqData={{
                  ...additionalReqData,
                  first_name: userInfo.first_name,
                  last_name: userInfo.last_name,
                  email: userInfo.email,
                  phone: userInfo.mobile_phone,
                  url: window.location.href,
                  issue_description: `User is missing a phone number for their H.O.G. membership. Member ID: ${memberInfoToBeConfirmed.ident}`,
                }}
                onSuccess={() => {
                  setTicketSubmitted(true);
                }}
              />
            )}
          </Stack>
          <Button
            size="md"
            fullWidth
            mt="lg"
            onClick={() => {
              setMemberInfoToBeConfirmed(null);
              setActiveStep('intro');
            }}
          >
            {t('common:try_again')}
          </Button>
        </div>
      );
    }

    return (
      <div>
        <MemberLookupConfirmation
          confirmationInfo={{
            ident: memberInfoToBeConfirmed.ident,
            label: t('mixer:hog_membership:member_lookup_confirmation_label'),
            value: memberInfoToBeConfirmed.phone_number
              ? memberInfoToBeConfirmed.phone_number.replace(/\D/g, '')
              : '',
            question: t(
              'mixer:hog_membership:member_lookup_confirmation_question'
            ),
            confirmation_button_text: t('common:yes'),
            decline_button_text: t('common:no'),
          }}
          onConfirmClick={() => onMemberConfirmation()}
          onDeclineClick={() => {
            setMemberInfoToBeConfirmed(null);
            setActiveStep('intro');
          }}
          loading={loading}
        />
      </div>
    );
  }

  return <Box></Box>;
}

const CreateFlow = ({
  reqData,
  showLead = false,
  setMembershipInfo = () => {},
  userInfo = null,
}) => {
  const { t } = useTranslation();
  const [showDealerCode, setShowDealerCode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    if (showLead) {
      return setLoading(false);
    }
    createMembership();
  }, []);

  function createMembership() {
    setLoading(true);
    setError(null);
    createThirdPartyLoyaltyMembership({
      ...reqData,
      variety: 1,
    })
      .then((data) => {
        if (!data.success) {
          setCreateError(data);
        } else {
          setMembershipInfo(data);
        }
      })
      .catch((err) => {
        const errorMessage =
          err?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          String(err);
        setError(errorMessage);
        setLoading(false);
      });
  }

  function createLead(remoteCodeData = null) {
    const req = {
      ...reqData,
      variety: 1,
    };
    delete req.check;

    if (remoteCodeData) {
      req.remote_code_id = remoteCodeData.id;
    }

    axios
      .post(`/leads/`, req)
      .then(() => {
        createMembership();
      })
      .catch((err) => {
        // toast.error(err);
        // onLeadCreate();
        setError(err);
      });
  }

  if (error) {
    return (
      <Stack>
        <Text c="white" fw={600} align="center" size="xl">
          {error}
        </Text>
        <Button
          size="lg"
          onClick={() => {
            setError(null);
            createMembership();
          }}
        >
          {t('common:try_again')}
        </Button>
      </Stack>
    );
  }

  if (createError) {
    return (
      <HogMembershipCreateError
        serverMessage={createError.message}
        errorCode={createError.error_code}
        ticketData={{
          ...reqData,
          ...userInfo,
          phone: userInfo?.mobile_phone,
        }}
      />
    );
  }

  if (showDealerCode) {
    return (
      <div>
        <DealerVerification
          setDealerCodeData={(e) => {
            createLead(e);
          }}
        />
        <Flex justify="center" mt="xl">
          <Button
            size="xs"
            c="dark"
            color="gray.1"
            radius="lg"
            onClick={() => setShowDealerCode(false)}
          >
            {t('common:back')}
          </Button>
        </Flex>
      </div>
    );
  }

  if (showLead) {
    return (
      <Stack>
        <Text c="white" fw={600} align="center" size="xl">
          {t('mixer:hog_membership:are_you_working_with_an_hd_rep')}
        </Text>
        <Flex justify="center" gap="sm" direction="column">
          <Button onClick={() => setShowDealerCode(true)} size="md">
            {t('common:yes')}
          </Button>
          <Button
            color="red"
            onClick={() => createLead()}
            size="md"
            loading={loading}
          >
            {t('common:no')}
          </Button>
        </Flex>
      </Stack>
    );
  }

  return (
    <div>
      <LoadingScreen />
    </div>
  );
};

const MemberLookup = ({
  additionalReqData,
  setMemberInfoToBeConfirmed,
  children,
  setMembershipInfo = () => {},
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);

  function onSubmit() {
    setLoading(true);

    let formattedValue = value.toUpperCase();
    // if (formattedValue.slice(0, 2) !== "US") {
    //   formattedValue = "US" + formattedValue;
    // }

    const req = {
      ...additionalReqData,
      loyalty_membership_ident: formattedValue,
      ident: formattedValue,
      ident_only: true,
      user_id: additionalReqData.user_id,
      variety: 1,
      error_flow: true,
    };

    thirdPartyUserLookup(req)
      .then((response) => {
        const res = response[0];
        if (!res) {
          setError(t('mixer:hog_membership:membership_not_found_text'));
          setLoading(false);
          return;
        }

        if (res?.verified && res?.phone_match) {
          return onVerifiedAndPhoneMatch({
            ...req,
            ...res,
          });
        } else {
          setMemberInfoToBeConfirmed({
            ...res,
            loyalty_membership_ident: formattedValue,
            ident: formattedValue,
            user_id: additionalReqData.user_id,
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        // toast.error(err);
        // setLoading(false);
        setError(err);
        setLoading(false);
      });
  }

  function onVerifiedAndPhoneMatch(usrLookupData) {
    createLoyaltyMembership({
      ...usrLookupData,
    })
      .then(({ data }) => {
        setMembershipInfo(data);
      })
      .catch((err) => {
        // toast.error(err);
        // setLoading(false);
        setError(err);
        setLoading(false);
      });
  }

  if (error) {
    return (
      <Stack>
        <Text c="white" fw={600} align="center" size="xl">
          {error}
        </Text>
        <Button size="lg" onClick={() => setError(null)}>
          {t('common:try_again')}
        </Button>
      </Stack>
    );
  }
  return (
    <div>
      <Stack>
        <Text align="center" c="white" fw="bold" size="xl">
          {t('mixer:hog_membership:member_id_title')}
          <br />
          (e.g. US1234567)
        </Text>
        <TextInput
          placeholder={t('mixer:hog_membership:member_id_placeholder')}
          value={value}
          onChange={(e) => {
            if (e.target.value.length > 20) {
              return;
            }
            setValue(e.target.value.trim().toUpperCase());
          }}
          size="lg"
          styles={{
            input: {
              textAlign: 'center',
            },
          }}
        />
        <Button
          loading={loading}
          fullWidth
          disabled={!value}
          onClick={() => onSubmit()}
          size="lg"
        >
          {t('common:submit')}
        </Button>
      </Stack>
      <Divider my="lg" opacity={0.1} />
      {children}
    </div>
  );
};

const MemberLookupConfirmation = ({
  confirmationInfo,
  onConfirmClick,
  onDeclineClick,
  loading = false,
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <Stack>
        {confirmationInfo.ident && (
          <Title order={1} c="white" fw={600} align="center">
            {confirmationInfo.ident}
          </Title>
        )}
        <Text size="xl" align="center" c="white" maw="90%" mx="auto">
          {confirmationInfo.label} <b>{confirmationInfo.value}</b>.
        </Text>
        <Text size="xl" align="center" c="white">
          {confirmationInfo.question}
        </Text>
        <Button onClick={onConfirmClick} size="md" loading={loading}>
          {confirmationInfo.confirmation_button_text}
        </Button>
        <Button
          size="md"
          color="red"
          onClick={onDeclineClick}
          disabled={loading}
        >
          {confirmationInfo.decline_button_text || t('common:no')}
        </Button>
      </Stack>
    </div>
  );
};

const LoadingScreen = () => {
  return (
    <div>
      <Flex justify="center" style={{ minHeight: '300px' }} align="center">
        <Loader size="xl" variant="dots" color="gray" />
      </Flex>
    </div>
  );
};

const CreateFlowButtons = ({ setActiveStep }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  return (
    <Flex justify="center" gap="xs">
      <Button
        size="xs"
        c="dark"
        color="gray.1"
        radius="lg"
        onClick={() => setActiveStep('intro')}
      >
        {t('common:back')}
      </Button>
      <Button
        size="xs"
        c="dark"
        color="gray.1"
        radius="lg"
        onClick={() => setShowModal(true)}
      >
        {t('mixer:hog_membership:cant_find_hog_id_button')}
      </Button>
      <div>
        <Modal opened={showModal} onClose={() => setShowModal(false)}>
          <Stack>
            <Text>{t('mixer:hog_membership:cant_find_hog_id_modal_text')}</Text>
            <Text>
              {t('mixer:hog_membership:cant_find_hog_id_modal_text_2')}
            </Text>
            <Button component="a" href="tel:18002582464">
              {t('mixer:hog_membership:cant_find_hog_id_modal_button')}
            </Button>
          </Stack>
        </Modal>
      </div>
    </Flex>
  );
};

const NeedHelpFlow = ({ submitted = false, reqData, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  function onClick() {
    setLoading(true);
    submitTroubleTicket(reqData)
      .then(() => {
        toast.success(
          t('mixer:hog_membership:help_ticket_submitted_successfully')
        );
        onSuccess();
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <>
      {submitted ? (
        <Text c="green" fw={600} align="center">
          {t('mixer:hog_membership:help_ticket_submitted_successfully_text')}
        </Text>
      ) : (
        <div>
          {loading ? (
            <Flex justify="center" align="center">
              <Loader size="sm" color="orange" />
            </Flex>
          ) : (
            <Text c="white" align="center">
              {t('mixer:hog_membership:need_more_help')}{' '}
              <UnstyledButton
                disabled={loading}
                style={{
                  fontWeight: 600,
                  color: 'var(--mantine-color-orange-6)',
                }}
                onClick={onClick}
              >
                {t('mixer:hog_membership:click_here')}
              </UnstyledButton>{' '}
              {t('mixer:hog_membership:to_open_a_support_ticket')}.
            </Text>
          )}
        </div>
      )}
    </>
  );
};
